import * as zod from 'zod';

import type { BaseFeatureFlagDefinition, Section } from './featureFlags';

export type InternalFeatureFlagDefinitionSchema = BaseFeatureFlagDefinition & {
  section?: Section;
};

// Internal testing feature flags
export const InternalFeatureFlagDataZod = zod.object({
  adHocFullSpeed: zod.boolean().default(false),
  testingSafetyLimits: zod.boolean().default(false),
  sanitizeBlackboxCan: zod.boolean().default(false),
  enableFoxgloveServer: zod.boolean().default(false),
  disableEstopHealthCheck: zod.boolean().default(false),
  dualModeCollisionDisabled: zod.boolean().default(false),
  locateStepTextPrompt: zod.boolean().default(false),
  showStackTraceInErrorBoundary: zod.boolean().default(false),
  visualizeTooltipPath: zod.boolean().default(false),
  doNotResetPositionOnTrajectoryReset: zod.boolean().default(true),
  enableJointStoppingAccelerations: zod.boolean().default(false),
  disableErrorOnInvalidTrajectory: zod.boolean().default(false),
  thorPedestalMount: zod.boolean().default(true),
  treatCanInvalidAsWarning: zod.boolean().default(false),
  movementAnalyzer: zod.boolean().default(false),
  disableMotionPlanCache: zod.boolean().default(false),
  botmanEventLoopProfiler: zod.boolean().default(false),
  botmanHeapAllocProfiler: zod.boolean().default(false),
  botmanTraceGcNvp: zod.boolean().default(false),
  botmanTraceGcObjectStats: zod.boolean().default(false),
  botmanRosActionProfiler: zod.boolean().default(false),
  ro1LegacySupport: zod.boolean().default(false),
  controlBoxResourceMetrics: zod.boolean().default(true),
  newSolenoid: zod.boolean().default(false),
});

export type InternalFeatureFlagData = zod.infer<
  typeof InternalFeatureFlagDataZod
>;
export type InternalFeatureFlagKey = keyof InternalFeatureFlagData;

export const internalFeatureFlagDefinitions: Record<
  InternalFeatureFlagKey,
  InternalFeatureFlagDefinitionSchema
> = {
  adHocFullSpeed: {
    requiresRestart: false,
    title: 'Full Speed Adhoc Movements',
    section: 'movement',
    allowSingleUser: true,
    description: 'Enable full speed adhoc movements',
  },
  testingSafetyLimits: {
    requiresRestart: false,
    title: 'Testing Safety Limits',
    description: 'Unlock safety limits for RO3 testing',
    section: 'safety',
    allowSingleUser: false,
  },
  sanitizeBlackboxCan: {
    title: 'Sanitize Blackbox CAN',
    section: 'infra',
    allowSingleUser: false,
    description: 'Replace NaN values in Blackbox CAN payloads before logging',
  },
  enableFoxgloveServer: {
    title: 'Enable Foxglove Server',
    section: 'infra',
    allowSingleUser: false,
    description: 'Enable the Foxglove server',
  },
  disableEstopHealthCheck: {
    title: 'Disable pre-unbrake Estop check',
    section: 'safety',
    allowSingleUser: false,
  },
  dualModeCollisionDisabled: {
    title: 'Disable IMU acceleration threshold detection',
    section: 'safety',
    allowSingleUser: false,
  },
  locateStepTextPrompt: {
    title: 'Locate Step Text Prompting',
    section: 'vision',
    allowSingleUser: false,
    description:
      'Adds a text prompt to the locate step for recognition of objects in 3D space',
  },
  showStackTraceInErrorBoundary: {
    requiresRestart: false,
    title: 'Show Stack Traces in Error Boundary',
    section: 'infra',
    allowSingleUser: true,
    description:
      'Display error stack traces in a collapsible section when errors are caught by the error boundary',
  },
  visualizeTooltipPath: {
    requiresRestart: false,
    title: 'Visualize Tooltip Path',
    section: 'movement',
    allowSingleUser: true,
    description: 'Visualize the path of the tooltip in the visualizer',
  },
  doNotResetPositionOnTrajectoryReset: {
    title: 'Reset Only Velocity on Trajectory Reset',
    section: 'movement',
    allowSingleUser: false,
    description: 'Reset only the velocity on trajectory reset',
  },
  enableJointStoppingAccelerations: {
    requiresRestart: false,
    title: 'Enable Joint Stopping Accelerations',
    section: 'safety',
    allowSingleUser: false,
    description:
      'Show per-joint stopping acceleration controls in safety settings. The backend always uses jointStoppingAccelerations; this flag only controls UI visibility.',
  },
  disableErrorOnInvalidTrajectory: {
    title: 'Disable Error on Invalid Trajectory',
    section: 'safety',
    allowSingleUser: false,
    description: 'Disable error on invalid trajectory',
  },
  thorPedestalMount: {
    title: 'Thor Pedestal Mount',
    section: 'safety',
    allowSingleUser: false,
    description:
      'Enable pedestal mount mode for Thor robot models (disables floor collision boundary)',
  },
  treatCanInvalidAsWarning: {
    title: 'Treat CAN Invalid as Warning',
    section: 'safety',
    allowSingleUser: false,
    description:
      'Treat CAN Invalid Message ResultType as warning level instead of error level',
  },
  movementAnalyzer: {
    title: 'Movement Analyzer',
    section: 'movement',
    allowSingleUser: false,
    description: 'Enable movement analyzer functionality in kalibrate-bot',
  },
  disableMotionPlanCache: {
    title: 'Disable Motion Plan Cache',
    section: 'movement',
    allowSingleUser: false,
    description:
      'Completely disable the motion plan cache. Every motion plan request will be computed fresh. Useful for debugging motion planning issues.',
  },
  botmanEventLoopProfiler: {
    title: 'Botman Event Loop Profiler',
    section: 'infra',
    allowSingleUser: false,
    description:
      'Enables a rolling event-loop CPU profiler that saves up to 5s of pre-spike samples to /tmp/botman-profiles/ when loop lag exceeds 200ms in a 1s window (one save per 60s, keeps 10 most recent). Use to diagnose heartbeat drift.',
  },
  botmanHeapAllocProfiler: {
    title: 'Botman Heap Allocation Profiler',
    section: 'infra',
    allowSingleUser: false,
    description:
      'Enables a rolling V8 heap allocation sampler (512KB interval) that saves up to 5s of pre-spike samples to /tmp/botman-heap-profiles/ when loop lag exceeds 200ms in a 1s window (one save per 60s, keeps 10 most recent). Output is a .heapprofile loadable in Chrome DevTools (Memory → Load profile) showing which call sites allocated the bytes. Use to diagnose allocation-driven GC pressure or leaks.',
  },
  botmanTraceGcNvp: {
    title: 'Botman --trace-gc-nvp',
    section: 'infra',
    allowSingleUser: false,
    description:
      'Adds --trace-gc-nvp to botman startup (requires restart). Writes one parseable line per GC with timing + heap stats.',
  },
  botmanTraceGcObjectStats: {
    title: 'Botman --trace-gc-object-stats',
    section: 'infra',
    allowSingleUser: false,
    description:
      'Adds --trace-gc-object-stats to botman startup (requires restart). Emits a per-type breakdown (JS_OBJECT_TYPE, JS_FUNCTION_TYPE, STRING_TYPE, ...) at each GC. Adds ~100-300 lines per GC and a single-digit % cost to each pause — fine for an investigation window, not a long-term default.',
  },
  botmanRosActionProfiler: {
    title: 'Botman ROS Action Profiler',
    section: 'infra',
    allowSingleUser: false,
    description:
      'Logs per-phase timings (createGoal / getClient / goal acceptance / execution) for each ROS action goal in ExecuteAction/BaseAction. Lightweight (hrtime + one log line per completed goal). Use to locate where ROS action latency originates when diagnosing heartbeat drift.',
  },
  ro1LegacySupport: {
    title: 'RO1 Legacy Support',
    section: 'movement',
    allowSingleUser: false,
    description:
      'Legacy support for RO1 arms on older firmware: disables cascaded motor control and skips the unbrake motion-verification wiggle (unbrake distance 0). NOTE: also enable "Disable Dynamic Hardware Config" on these robots.',
  },
  controlBoxResourceMetrics: {
    title: 'Control Box Resource Metrics',
    section: 'infra',
    allowSingleUser: false,
    description:
      'Enable Prometheus + cAdvisor monitoring stack for per-container resource metrics',
  },
  newSolenoid: {
    title: 'New Solenoid (Rev C)',
    section: 'safety',
    allowSingleUser: false,
    description:
      'This robot has Rev C brake solenoids fitted (Thor). Written to each ' +
      'joint’s flash metadata at the next firmware install; motor-board ' +
      'firmware then drives the brake with Rev C duty cycles. Only enable ' +
      'after EVERY joint’s solenoid has been physically swapped to Rev C ' +
      '— Rev C drive on a Rev B solenoid can brown out the motor board.',
  },
};

type GroupedKeys = { section: Section; keys: InternalFeatureFlagKey[] }[];

export const internalTestingFeatureFlags: GroupedKeys = (() => {
  const grouped: GroupedKeys = [];

  for (const keyStr of Object.keys(internalFeatureFlagDefinitions)) {
    const key = keyStr as InternalFeatureFlagKey;
    let { section } = internalFeatureFlagDefinitions[key];

    if (!section) {
      section = 'other';
    }

    const group = grouped.find((g) => g.section === section);

    if (group) {
      group.keys.push(key);
    } else {
      grouped.push({ section, keys: [key] });
    }
  }

  // sort by section
  return grouped.sort((a, b) => {
    if (a.section === 'other') {
      return 1;
    }

    if (b.section === 'other') {
      return -1;
    }

    return a.section.localeCompare(b.section);
  });
})();
